import type { DefaultAutomationTemplateMetadata } from '@reach/shared-types/automation-template';
import React, { useState } from 'react';
import { useCreatePartnerAutomationFromDefault } from './useCreateTemplate';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { BaseSelection } from './BaseSelection';
import { TemplateForm } from './TemplateForm';
import styles from './CreateTemplateDialog.module.css';

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTemplateCreated: (templateId: string) => void;
  defaultTemplates?: DefaultAutomationTemplateMetadata[];
}

type DialogStep = 'selection' | 'customize';

export const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  open,
  onOpenChange,
  onTemplateCreated,
  defaultTemplates = [],
}) => {
  const [step, setStep] = useState<DialogStep>('selection');
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const createFromDefaultMutation = useCreatePartnerAutomationFromDefault();

  const handleClose = () => {
    setStep('selection');
    setSelectedTemplateName(null);
    setTemplateName('');
    setDescription('');
    onOpenChange(false);
  };

  const handleSelectTemplate = (templateName: string) => {
    setSelectedTemplateName(templateName);
    setStep('customize');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { created, templateId } = await createFromDefaultMutation.mutateAsync({
      defaultTemplateName: selectedTemplateName!,
      customName: templateName,
      customDescription: description,
    });

    if (created && templateId) {
      setTemplateName('');
      setDescription('');
      handleClose();
      onTemplateCreated(templateId);
    }
  };

  const handleBack = () => {
    setStep('selection');
    setSelectedTemplateName(null);
    setTemplateName('');
    setDescription('');
  };

  if (!open) return null;

  const isLoading = createFromDefaultMutation.isPending;

  return (
    <div className={styles.createTemplateDialog}>
      <DialogBackdrop onClose={handleClose} />

      {step === 'selection' ? (
        <BaseSelection
          templates={defaultTemplates}
          onSelectTemplate={handleSelectTemplate}
          onClose={handleClose}
        />
      ) : (
        <TemplateForm
          templateName={templateName}
          description={description}
          onTemplateNameChange={setTemplateName}
          onDescriptionChange={setDescription}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          onClose={handleClose}
          isLoading={isLoading}
          requiresDescription={true}
        />
      )}
    </div>
  );
};
